// ======================================================================
// FILE:
// src/modules/finance/transactions/payment_voucher/pages/PaymentVoucherViewPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Payment Voucher - View
//
// PURPOSE:
// Display a Finance Payment Voucher in read-only mode.
//
// IMPORTANT:
// - No accounting logic
// - No direct database access
// - Uses paymentVoucherService
// - Draft vouchers can be edited/submitted
// - Submitted vouchers can be approved
// - Approved vouchers can be posted
// ======================================================================

import React, {
    useEffect,
    useState,
} from "react";

import {
    useNavigate,
    useParams,
} from "react-router-dom";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import FinanceStatusBadge
    from "../../../components/FinanceStatusBadge";

import paymentVoucherService
    from "../services/paymentVoucherService";


// ======================================================================
// COMPONENT
// ======================================================================

export default function PaymentVoucherViewPage() {

    const navigate =
        useNavigate();

    const {
        id
    } = useParams();


    // ==================================================================
    // STATE
    // ==================================================================

    const [
        voucher,
        setVoucher
    ] = useState(null);


    const [
        loading,
        setLoading
    ] = useState(true);


    const [
        errorMessage,
        setErrorMessage
    ] = useState("");


    const [
        actionLoading,
        setActionLoading
    ] = useState(false);


    // ==================================================================
    // LOAD
    // ==================================================================

    useEffect(
        () => {

            loadVoucher();

        },
        [
            id
        ]
    );


    async function loadVoucher() {

        try {

            setLoading(true);

            setErrorMessage("");


            const response =
                await paymentVoucherService
                    .getPaymentVoucher(
                        id
                    );


            setVoucher(
                response
            );

        }
        catch (
            error
        ) {

            console.error(
                "Failed to load Payment Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to load Payment Voucher."
            );

        }
        finally {

            setLoading(false);

        }

    }


    // ==================================================================
    // NAVIGATION
    // ==================================================================

    function handleBack() {

        navigate(
            "/finance/payment-vouchers"
        );

    }


    function handleEdit() {

        navigate(
            `/finance/payment-vouchers/edit/${id}`
        );

    }


    // ==================================================================
    // SUBMIT
    // ==================================================================

    async function handleSubmit() {

        if (
            !voucher?.company_id
        ) {

            setErrorMessage(
                "Company is required."
            );

            return;

        }


        try {

            setActionLoading(true);

            setErrorMessage("");


            await paymentVoucherService
                .submitPaymentVoucher(
                    voucher.id,
                    voucher.company_id
                );


            await loadVoucher();

        }
        catch (
            error
        ) {

            console.error(
                "Failed to submit Payment Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to submit Payment Voucher."
            );

        }
        finally {

            setActionLoading(false);

        }

    }


    // ==================================================================
    // APPROVE
    // ==================================================================

    async function handleApprove() {

        if (
            !voucher?.company_id
        ) {

            setErrorMessage(
                "Company is required."
            );

            return;

        }


        try {

            setActionLoading(true);

            setErrorMessage("");


            await paymentVoucherService
                .approvePaymentVoucher(
                    voucher.id,
                    voucher.company_id
                );


            await loadVoucher();

        }
        catch (
            error
        ) {

            console.error(
                "Failed to approve Payment Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to approve Payment Voucher."
            );

        }
        finally {

            setActionLoading(false);

        }

    }


    // ==================================================================
    // POST
    // ==================================================================

    async function handlePost() {

        if (
            !voucher?.company_id
        ) {

            setErrorMessage(
                "Company is required."
            );

            return;

        }


        try {

            setActionLoading(true);

            setErrorMessage("");


            await paymentVoucherService
                .postPaymentVoucher(
                    voucher.id,
                    voucher.company_id
                );


            await loadVoucher();

        }
        catch (
            error
        ) {

            console.error(
                "Failed to post Payment Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to post Payment Voucher."
            );

        }
        finally {

            setActionLoading(false);

        }

    }


    // ==================================================================
    // FORMAT DATE
    // ==================================================================

    function formatDate(
        value
    ) {

        if (
            !value
        ) {

            return "-";

        }


        return String(
            value
        ).slice(
            0,
            10
        );

    }


    // ==================================================================
    // FORMAT MONEY
    // ==================================================================

    function formatAmount(
        value
    ) {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN",
            {
                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,
            }
        );

    }


    // ==================================================================
    // LABEL
    // ==================================================================

    function formatPayeeType(
        value
    ) {

        if (
            !value
        ) {

            return "-";

        }


        return String(
            value
        )
            .replaceAll(
                "_",
                " "
            )
            .replace(
                /\b\w/g,
                char =>
                    char.toUpperCase()
            );

    }


    // ==================================================================
    // LOADING
    // ==================================================================

    if (
        loading
    ) {

        return (
            <div
                className="
                    d-flex
                    justify-content-center
                    align-items-center
                    p-5
                "
            >

                <div
                    className="
                        spinner-border
                        text-primary
                    "
                    role="status"
                />

            </div>
        );

    }


    // ==================================================================
    // ERROR / NOT FOUND
    // ==================================================================

    if (
        !voucher
    ) {

        return (
            <>

                <FinancePageHeader
                    icon="fas fa-money-check-alt"

                    title="Payment Voucher"

                    subtitle={
                        "View Finance Payment Voucher."
                    }

                    breadcrumbs={[
                        {
                            label:
                                "Finance",

                            to:
                                "/finance",
                        },

                        {
                            label:
                                "Payment Voucher",

                            to:
                                "/finance/payment-vouchers",
                        },

                        {
                            label:
                                "View",
                        },
                    ]}

                    actions={
                        <button
                            type="button"

                            className="
                                btn
                                btn-outline-secondary
                            "

                            onClick={
                                handleBack
                            }
                        >

                            <i
                                className="
                                    fas
                                    fa-arrow-left
                                    me-1
                                "
                            />

                            Back to List

                        </button>
                    }
                />


                <div
                    className="
                        alert
                        alert-danger
                        mt-3
                    "
                >

                    {
                        errorMessage ||
                        "Payment Voucher not found."
                    }

                </div>

            </>
        );

    }


    // ==================================================================
    // STATUS
    // ==================================================================

    const status =
        String(
            voucher.status ||
            ""
        ).toUpperCase();


    const approvalStatus =
        String(
            voucher.approval_status ||
            ""
        ).toUpperCase();


    // ==================================================================
    // RENDER
    // ==================================================================

    return (
        <>

            {/* ==========================================================
                HEADER
            ========================================================== */}

            <FinancePageHeader

                icon="fas fa-money-check-alt"

                title={
                    `Payment Voucher ${voucher.voucher_no || ""}`
                }

                subtitle={
                    "View Finance Payment Voucher."
                }

                breadcrumbs={[
                    {
                        label:
                            "Finance",

                        to:
                            "/finance",
                    },

                    {
                        label:
                            "Payment Voucher",

                        to:
                            "/finance/payment-vouchers",
                    },

                    {
                        label:
                            voucher.voucher_no ||
                            "View",
                    },
                ]}

                actions={

                    <div
                        className="
                            d-flex
                            gap-2
                        "
                    >

                        <button
                            type="button"

                            className="
                                btn
                                btn-outline-secondary
                            "

                            onClick={
                                handleBack
                            }

                            disabled={
                                actionLoading
                            }
                        >

                            <i
                                className="
                                    fas
                                    fa-arrow-left
                                    me-1
                                "
                            />

                            Back to List

                        </button>


                        {
                            status === "DRAFT" && (

                                <button
                                    type="button"

                                    className="
                                        btn
                                        btn-outline-primary
                                    "

                                    onClick={
                                        handleEdit
                                    }

                                    disabled={
                                        actionLoading
                                    }
                                >

                                    <i
                                        className="
                                            fas
                                            fa-edit
                                            me-1
                                        "
                                    />

                                    Edit

                                </button>

                            )
                        }


                        {
                            status === "DRAFT" && (

                                <button
                                    type="button"

                                    className="
                                        btn
                                        btn-primary
                                    "

                                    onClick={
                                        handleSubmit
                                    }

                                    disabled={
                                        actionLoading
                                    }
                                >

                                    <i
                                        className="
                                            fas
                                            fa-paper-plane
                                            me-1
                                        "
                                    />

                                    {
                                        actionLoading
                                            ? "Processing..."
                                            : "Submit"
                                    }

                                </button>

                            )
                        }


                        {
                            status === "SUBMITTED" && (

                                <button
                                    type="button"

                                    className="
                                        btn
                                        btn-success
                                    "

                                    onClick={
                                        handleApprove
                                    }

                                    disabled={
                                        actionLoading
                                    }
                                >

                                    <i
                                        className="
                                            fas
                                            fa-check
                                            me-1
                                        "
                                    />

                                    {
                                        actionLoading
                                            ? "Processing..."
                                            : "Approve"
                                    }

                                </button>

                            )
                        }


                        {
                            status === "APPROVED" && (

                                <button
                                    type="button"

                                    className="
                                        btn
                                        btn-info
                                    "

                                    onClick={
                                        handlePost
                                    }

                                    disabled={
                                        actionLoading
                                    }
                                >

                                    <i
                                        className="
                                            fas
                                            fa-check-double
                                            me-1
                                        "
                                    />

                                    {
                                        actionLoading
                                            ? "Processing..."
                                            : "Post"
                                    }

                                </button>

                            )
                        }

                    </div>

                }

            />


            {/* ==========================================================
                ERROR
            ========================================================== */}

            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            mt-3
                        "
                    >

                        {errorMessage}

                    </div>

                )
            }


            {/* ==========================================================
                STATUS SUMMARY
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="
                        card-body
                    "
                >

                    <div
                        className="
                            d-flex
                            justify-content-between
                            align-items-center
                            flex-wrap
                            gap-3
                        "
                    >

                        <div>

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                Voucher No.
                            </div>

                            <div
                                className="
                                    fw-semibold
                                "
                            >
                                {
                                    voucher.voucher_no ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div>

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                Status
                            </div>

                            <FinanceStatusBadge
                                type="journal"
                                value={
                                    voucher.status
                                }
                            />

                        </div>


                        <div>

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                Approval
                            </div>

                            <FinanceStatusBadge
                                type="approval"
                                value={
                                    voucher.approval_status
                                }
                            />

                        </div>


                        <div
                            className="
                                text-end
                            "
                        >

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                Total Payment
                            </div>

                            <div
                                className="
                                    fw-bold
                                    fs-5
                                    text-primary
                                "
                            >

                                {
                                    formatAmount(
                                        voucher.transaction_amount
                                    )
                                }

                                {" "}

                                {
                                    voucher.currency_code ||
                                    ""
                                }

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                VOUCHER DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="
                        card-body
                    "
                >

                    <h6
                        className="
                            text-primary
                            border-bottom
                            pb-2
                            mb-3
                        "
                    >
                        Voucher Details
                    </h6>


                    <div
                        className="
                            row
                            g-3
                        "
                    >

                        <div
                            className="
                                col-md-3
                            "
                        >

                            <label
                                className="
                                    form-label
                                    text-muted
                                    small
                                "
                            >
                                Company
                            </label>

                            <div
                                className="
                                    form-control
                                    bg-light
                                "
                            >
                                {
                                    voucher.company_name ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <label
                                className="
                                    form-label
                                    text-muted
                                    small
                                "
                            >
                                Voucher Date
                            </label>

                            <div
                                className="
                                    form-control
                                    bg-light
                                "
                            >
                                {
                                    formatDate(
                                        voucher.voucher_date
                                    )
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <label
                                className="
                                    form-label
                                    text-muted
                                    small
                                "
                            >
                                Payee Type
                            </label>

                            <div
                                className="
                                    form-control
                                    bg-light
                                "
                            >
                                {
                                    formatPayeeType(
                                        voucher.payee_type
                                    )
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <label
                                className="
                                    form-label
                                    text-muted
                                    small
                                "
                            >
                                Payee
                            </label>

                            <div
                                className="
                                    form-control
                                    bg-light
                                "
                            >
                                {
                                    voucher.payee_name ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <label
                                className="
                                    form-label
                                    text-muted
                                    small
                                "
                            >
                                Payment Mode
                            </label>

                            <div
                                className="
                                    form-control
                                    bg-light
                                "
                            >
                                {
                                    voucher.payment_mode ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <label
                                className="
                                    form-label
                                    text-muted
                                    small
                                "
                            >
                                Payment Account
                            </label>

                            <div
                                className="
                                    form-control
                                    bg-light
                                "
                            >
                                {
                                    voucher.payment_account_name ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <label
                                className="
                                    form-label
                                    text-muted
                                    small
                                "
                            >
                                Account Number
                            </label>

                            <div
                                className="
                                    form-control
                                    bg-light
                                "
                            >
                                {
                                    voucher.payment_account_number ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <label
                                className="
                                    form-label
                                    text-muted
                                    small
                                "
                            >
                                Currency
                            </label>

                            <div
                                className="
                                    form-control
                                    bg-light
                                "
                            >
                                {
                                    voucher.currency_code ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <label
                                className="
                                    form-label
                                    text-muted
                                    small
                                "
                            >
                                Exchange Rate
                            </label>

                            <div
                                className="
                                    form-control
                                    bg-light
                                "
                            >
                                {
                                    voucher.exchange_rate ??
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <label
                                className="
                                    form-label
                                    text-muted
                                    small
                                "
                            >
                                Reference No.
                            </label>

                            <div
                                className="
                                    form-control
                                    bg-light
                                "
                            >
                                {
                                    voucher.reference_no ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <label
                                className="
                                    form-label
                                    text-muted
                                    small
                                "
                            >
                                Reference Date
                            </label>

                            <div
                                className="
                                    form-control
                                    bg-light
                                "
                            >
                                {
                                    formatDate(
                                        voucher.reference_date
                                    )
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-6
                            "
                        >

                            <label
                                className="
                                    form-label
                                    text-muted
                                    small
                                "
                            >
                                Remarks
                            </label>

                            <textarea
                                className="
                                    form-control
                                    bg-light
                                "

                                value={
                                    voucher.remarks ||
                                    ""
                                }

                                readOnly

                                rows="2"
                            />

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                ALLOCATION DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="
                        card-body
                    "
                >

                    <div
                        className="
                            d-flex
                            justify-content-between
                            align-items-center
                            mb-3
                        "
                    >

                        <h6
                            className="
                                text-primary
                                mb-0
                            "
                        >
                            Allocation Details
                        </h6>

                    </div>


                    <div
                        className="
                            table-responsive
                        "
                    >

                        <table
                            className="
                                table
                                table-bordered
                                table-sm
                                align-middle
                                mb-0
                            "
                        >

                            <thead
                                className="
                                    table-light
                                "
                            >

                                <tr>

                                    <th
                                        style={{
                                            width:
                                                "50px"
                                        }}
                                    >
                                        #
                                    </th>

                                    <th>
                                        GL Account
                                    </th>

                                    <th>
                                        Allocation
                                    </th>

                                    <th
                                        className="
                                            text-end
                                        "
                                    >
                                        Amount
                                    </th>

                                    <th>
                                        Cost Centre
                                    </th>

                                    <th>
                                        Description
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    Array.isArray(
                                        voucher.lines
                                    ) &&
                                    voucher.lines.length > 0
                                        ? voucher.lines.map(
                                            (
                                                line,
                                                index
                                            ) => (

                                                <tr
                                                    key={
                                                        line.id ||
                                                        index
                                                    }
                                                >

                                                    <td>
                                                        {
                                                            line.sequence_no ||
                                                            index + 1
                                                        }
                                                    </td>


                                                    <td>

                                                        <div
                                                            className="
                                                                fw-semibold
                                                            "
                                                        >
                                                            {
                                                                line.account_code ||
                                                                "-"
                                                            }

                                                            {
                                                                line.account_name
                                                                    ? ` - ${line.account_name}`
                                                                    : ""
                                                            }
                                                        </div>

                                                    </td>


                                                    <td>

                                                        {
                                                            line.allocation_type ||
                                                            "MANUAL"
                                                        }

                                                    </td>


                                                    <td
                                                        className="
                                                            text-end
                                                        "
                                                    >

                                                        {
                                                            formatAmount(
                                                                line.transaction_amount
                                                            )
                                                        }

                                                    </td>


                                                    <td>

                                                        {
                                                            line.cost_center_name ||
                                                            "-"
                                                        }

                                                    </td>


                                                    <td>

                                                        {
                                                            line.description ||
                                                            "-"
                                                        }

                                                    </td>

                                                </tr>

                                            )
                                        )
                                        : (

                                            <tr>

                                                <td
                                                    colSpan="6"
                                                    className="
                                                        text-center
                                                        text-muted
                                                        py-4
                                                    "
                                                >
                                                    No allocation lines found.
                                                </td>

                                            </tr>

                                        )
                                }

                            </tbody>

                        </table>

                    </div>


                    {/* ==================================================
                        TOTAL
                    ================================================== */}

                    <div
                        className="
                            d-flex
                            justify-content-end
                            mt-3
                        "
                    >

                        <div
                            className="
                                border
                                rounded
                                p-3
                            "
                            style={{
                                minWidth:
                                    "280px"
                            }}
                        >

                            <div
                                className="
                                    d-flex
                                    justify-content-between
                                "
                            >

                                <span>
                                    Total Payment
                                </span>

                                <strong>
                                    {
                                        formatAmount(
                                            voucher.transaction_amount
                                        )
                                    }
                                </strong>

                            </div>


                            <div
                                className="
                                    d-flex
                                    justify-content-between
                                    text-muted
                                    mt-1
                                "
                            >

                                <span>
                                    Base Amount
                                </span>

                                <span>
                                    {
                                        formatAmount(
                                            voucher.base_amount
                                        )
                                    }
                                </span>

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                AUDIT / POSTING INFORMATION
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                    mb-4
                "
            >

                <div
                    className="
                        card-body
                    "
                >

                    <h6
                        className="
                            text-primary
                            border-bottom
                            pb-2
                            mb-3
                        "
                    >
                        Processing Information
                    </h6>


                    <div
                        className="
                            row
                            g-3
                        "
                    >

                        <div
                            className="
                                col-md-3
                            "
                        >

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                Created By
                            </div>

                            <div
                                className="
                                    fw-semibold
                                "
                            >
                                {
                                    voucher.created_by ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                Created At
                            </div>

                            <div
                                className="
                                    fw-semibold
                                "
                            >
                                {
                                    voucher.created_at ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                Approved By
                            </div>

                            <div
                                className="
                                    fw-semibold
                                "
                            >
                                {
                                    voucher.approved_by ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                Posted By
                            </div>

                            <div
                                className="
                                    fw-semibold
                                "
                            >
                                {
                                    voucher.posted_by ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                Journal No.
                            </div>

                            <div
                                className="
                                    fw-semibold
                                "
                            >
                                {
                                    voucher.journal_no ||
                                    "-"
                                }
                            </div>

                        </div>


                        <div
                            className="
                                col-md-3
                            "
                        >

                            <div
                                className="
                                    text-muted
                                    small
                                "
                            >
                                Journal Header ID
                            </div>

                            <div
                                className="
                                    fw-semibold
                                "
                            >
                                {
                                    voucher.journal_header_id ||
                                    "-"
                                }
                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </>
    );

}